import React, { useState, useRef, useEffect, useContext, useMemo } from "react";
import StackManagementDialog from "./StackManagementDialog";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import ActionIcon from "@mui/icons-material/MoreHoriz";
import CheckIcon from "@mui/icons-material/Check";

import MUIDataTable, { TableToolbar, TableFilterList } from "mui-datatables";

import {
  createTheme,
  ThemeProvider,
  StyledEngineProvider,
} from "@mui/material/styles";
import moment from "moment";
import { default as _last } from "lodash/last";

import { STATUS_COLORS } from "../../color/colors";

import Widget from "../../widget/Widget";
import { ISXContext } from "services/Utils";

import { STACKS_MANAGEMENT_ACTIONS } from "./actions";

const DATE_FORMAT = "DD MMM YYYY HH:mm:ss";
const DEFAULT_COLUMNS = [
  "label",
  "last_updated",
  "status",
  "location",
  "owner",
];

const tableMuiTheme = (theme) =>
  createTheme({
    ...theme,
    components: {
      ...theme.components,
      MUIDataTable: {
        styleOverrides: {
          paper: {
            display: "flex",
            flexFlow: "column",
            height: "100%",
          },
        },
      },
      MUIDataTableHeadCell: {
        styleOverrides: {
          root: {
            fontSize: "0.875rem",
            fontWeight: 500,
            color: "rgba(0, 0, 0, 0.875)",
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            paddingTop: 0,
            paddingBottom: 0,
            paddingLeft: 0,
            paddingRight: 0,
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            padding: 0,
          },
        },
      },
    },
  });

const getStatusFromLastUpdated = (now, lastUpdated, updateInterval) => {
  const diff = now.diff(moment(lastUpdated), "seconds");
  updateInterval = parseInt(updateInterval || "10");
  const intervals = diff / updateInterval;
  if (intervals < 4) {
    //changes from < 2 to < 4. < 2 was too aggressive in changing state to warning when even when there's a slight delay in updates.
    return "good";
  } else if (intervals < 10) {
    return "warning";
  } else {
    return "bad";
  }
};

// for better performance, make component "pure" (rerender only on shallow prop changes)
const MemoizedTable = React.memo(MUIDataTable);

const ReadonlyToolbar = (props) => {
  return (
    <Tooltip
      disableFocusListener
      title="You will need to unlock the dashboard to sort, filter or search"
    // placement="top"
    >
      <div style={{ pointerEvents: "auto", opacity: 0.5 }}>
        <div style={{ pointerEvents: "none" }}>
          <TableToolbar {...props} />
          <div style={{ height: 0, width: 0, overflow: "hidden" }}>
            <input autoFocus></input>
          </div>
        </div>
      </div>
    </Tooltip>
  );
};

const ReadonlyChip = (props) => {
  const { label } = props;
  return (
    <Tooltip title="You will need to unlock the dashboard to change filtering">
      <Chip label={label} onDelete={undefined} />
    </Tooltip>
  );
};

const ReadonlyTableFilterList = (props) => {
  return <TableFilterList {...props} ItemComponent={ReadonlyChip} />;
};

const StacksManagementWidget = (props) => {
  // const [detailsOpen, setDetailsOpen] = useState(false);
  const [action, setAction] = useState(null);
  const [stackMenuAnchor, setStackMenuAnchor] = useState(false);
  const [forceRender, setForceRender] = useState(false);

  const isxContext = useContext(ISXContext);

  const { widget = {}, updateWidgetOptions, stacks, readOnly } = props;
  const widgetOptions = widget.options || {};

  const selected = useRef(
    (widgetOptions.selected || []).reduce((acc, s) => {
      acc[s] = true;
      return acc;
    }, {})
  );

  const readOnlyRef = useRef(false);
  const searchText = useRef(null);
  const searchTextTimeout = useRef(null);
  const lastWidgetOptions = useRef(null);
  const selectedStack = useRef(null);
  const tableRef = useRef();

  const toggleRowSelect = (guuid) => {
    if (readOnlyRef.current) {
      return;
    }
    const checked = !selected.current[guuid];
    const updatedSelected = { ...selected.current, [guuid]: checked };
    selected.current = updatedSelected;
    // force table rerender
    setForceRender((f) => !f);
    const updatedSelectedOption = Object.entries(updatedSelected)
      .filter((s) => s[1])
      .map((s) => s[0]);
    updateWidgetOptions({ selected: updatedSelectedOption });
  };

  const columns = useRef(
    [
      {
        name: "actions",
        label: "actions",
        id: "actions",
        options: {
          empty: true,
          sort: false,
          filter: false,
          customBodyRender: (value, tableMeta, updateValue) => (
            <IconButton
              title="Click for menu of actions"
              onClick={(e) => {
                const stackId = _last(tableMeta.rowData);
                selectedStack.current = stackId;
                // setDetailsOpen(true);
                setStackMenuAnchor(e.currentTarget);
              }}
              style={{ padding: 6 }}
            >
              <ActionIcon />
            </IconButton>
          ),
        },
      },
      {
        name: "selected",
        id: "selected",
        label: "Selected",
        options: {
          customHeadLabelRender: () => <CheckIcon fontSize="small" />,
          sort: false,
          filterType: "custom",
          filterList: [],
          customFilterListOptions: {
            render: (v) => {
              return v.length ? "selected only" : false;
            },
          },
          filterOptions: {
            names: [],
            logic: (selected, filters) => {
              return filters.length ? !selected : false;
            },
            display: (filterList, onChange, index, column) => {
              return (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!filterList[index].length}
                      onChange={(event) => {
                        if (event.target.checked) {
                          filterList[index].push("selected");
                        } else {
                          filterList[index].length = 0;
                        }
                        onChange(filterList[index], index, column);
                      }}
                    />
                  }
                  sx={{
                    ".MuiFormControlLabel-root": {
                      marginLeft: 0,
                      marginRight: 0,
                    },
                    ".MuiFormControlLabel-label": {
                      marginRight: 0.75,
                    },
                  }}
                  label="Selected Only"
                  labelPlacement="start"
                />
              );
            },
          },
          customBodyRender: (value, tableMeta, updateValue) => {
            return (
              <Checkbox
                checked={value}
                onChange={() => toggleRowSelect(_last(tableMeta.rowData))}
              />
            );
          },
        },
      },
    ]
      .concat(
        (DEFAULT_COLUMNS || []).map((attr, idx) => {
          return {
            name: attr,
            label: attr.replace("_", " "),
            id: attr,
            options: {
              filterList: [],
              customBodyRender: (value, tableMeta, updateValue) => {
                if (attr === "last_updated") {
                  return value ? moment(value).format(DATE_FORMAT) : "---";
                } else if (attr === "status") {
                  return (
                    <Box
                      // className={classes.statusCell}
                      sx={{
                        height: "1em",
                        width: "3em",
                        backgroundColor: STATUS_COLORS[value],
                      }}
                    />
                  );
                }
                return value;
              },
            },
          };
        })
      )
      .concat([
        {
          name: "guuid",
          id: "guuid",
          options: {
            display: "excluded",
            filter: false,
          },
        },
      ])
  );

  const options = useRef({
    elevation: 0,
    responsive: "standard",
    filter: true,
    search: true,
    filterType: "dropdown",
    selectableRows: "none",
    expandableRows: false,
    pagination: true,
    rowsPerPageOptions: [5, 10, 20],
    rowsPerPage: 20,
    print: false,
    download: false,
    viewColumns: false,
    rowHover: false,
    searchText: "",
    sortOrder: {},
    textLabels: {
      body: {
        toolTip: "Sort",
      },
    },
    setRowProps: (row, dataIndex) => {
      if (selectedStack.current && _last(row) === selectedStack.current) {
        return {
          style: { backgroundColor: "darkgray" },
        };
      }
      return null;
    },
    onSearchChange: (text) => {
      options.current.searchText = text;
      clearTimeout(searchTextTimeout.current);
      searchTextTimeout.current = setTimeout(() => {
        updateWidgetOptions({
          search: text || null,
        });
      }, 1000);
    },
    onSearchClose: () => {
      searchText.current = "";
      updateWidgetOptions({ search: null });
    },
    onColumnSortChange: (colname, sortdir) => {
      // options.current.sortOrder = {
      //   name: colname,
      //   direction: sortdir,
      // }
      if (readOnlyRef.current) {
        const [colname, sortdir] = lastWidgetOptions.current.sort || [
          "timestamp",
          "desc",
        ];
        // options.current.sortOrder = {
        //   name: colname,
        //   direction: sortdir,
        // }
        options.current = {
          ...options.current,
          sortOrder: {
            name: colname,
            direction: sortdir,
          },
        };
        setForceRender((f) => !f);
      } else {
        updateWidgetOptions({
          sort: [colname, sortdir],
        });
      }
    },
    onChangeRowsPerPage: (rows) => {
      options.current.rowsPerPage = rows;
    },
    onFilterChange: (col, filterList) => {
      const colIdx = columns.current.findIndex(
        (element) => element.name === col
      );
      if (colIdx !== -1) {
        columns.current[colIdx].options.filterList = filterList[colIdx];
      }
      const filters = columns.current.reduce((acc, col) => {
        if (col.options.filter !== false) {
          acc[col.name] = col.options.filterList;
        }
        return acc;
      }, {});
      updateWidgetOptions({ filters });
    },

    customToolbarSelect: (selectedRows, displayData, setSelectedRows) => null,

    // setFilterChipProps: (colIndex, colName, data) => {
    //   return {
    //     disabled: readOnly,
    //   };
    // },
  });

  useEffect(() => {
    // options.current.textLabels.body.toolTip = readOnly ? "You will need to unlock dashboard to sort" : "Sort";
    // options.current.setFilterChipProps = (colIndex, colName, data) => {
    //   return {
    //     disabled: readOnly,
    //   };
    // }
    readOnlyRef.current = readOnly;
    setForceRender((f) => !f);
  }, [readOnly]);

  const stackIds = useMemo(() => {
    return Object.keys(stacks || {}).sort();
  }, [stacks]);

  if (lastWidgetOptions.current !== widgetOptions) {
    lastWidgetOptions.current = widgetOptions;
    const newWidgetOptions = widgetOptions || {};

    const wfilters = newWidgetOptions.filters || {};
    columns.current.forEach((col) => {
      if (col.options.filter !== false) {
        col.options.filterList = wfilters[col.name] || [];
      }
    });

    const [colname, sortdir] = newWidgetOptions.sort || ["timestamp", "desc"];
    options.current.sortOrder = {
      name: colname,
      direction: sortdir,
    };
    // columns.current.forEach(
    //   (col) =>
    //     (col.options.sortDirection = col.name === colname ? sortdir : "none")
    // );

    options.current.searchText = newWidgetOptions.search || "";
  }

  const data = useMemo(() => {
    const now = moment();
    return stackIds.map((guuid) => {
      const st = stacks[guuid] || {};
      const lastUpdated = parseInt(st.st_time);
      const lastBPUpdateTime =
        st.lastbpuptime != null ? parseInt(st.lastbpuptime) : null;
      const status = getStatusFromLastUpdated(now, lastUpdated, st.upd_int);

      const devSession = JSON.parse(st.dev_session || "{}");
      return {
        guuid: st.guuid,
        selected: !!selected.current[guuid],
        label: st?.name || devSession["stack.label"] || "<Unnamed>",
        location: st?.location || devSession["stack.location"] || "<Unknown>",
        last_updated: lastUpdated,
        status,
        owner: st.g_owner,
        last_bp_update: lastBPUpdateTime,
        stack: st,
      };
    });
  }, [stackIds, stacks]);

  return (
    <>
      <Widget
        {...props}
        configurable={false}
        dataType="stacks"
        widgetTitle="Stacks Management"
      >
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={tableMuiTheme}>
            <MemoizedTable
              data={data}
              columns={columns.current}
              options={options.current}
              forceRender={forceRender}
              components={{
                TableToolbar: readOnly ? ReadonlyToolbar : TableToolbar,
                TableFilterList: readOnly
                  ? ReadonlyTableFilterList
                  : TableFilterList,
              }}
            />
          </ThemeProvider>
        </StyledEngineProvider>
      </Widget>
      {action !== null && (
        <StackManagementDialog
          {...props}
          action={action}
          dialogOpen={action !== null}
          closeDialog={() => setAction(null)}
          selectedStack={selectedStack.current}
          tableRef={tableRef}
        />
      )}
      <Menu
        id="stack-menu"
        anchorEl={stackMenuAnchor}
        keepMounted
        open={Boolean(stackMenuAnchor)}
        onClose={() => setStackMenuAnchor(null)}
      >
        {STACKS_MANAGEMENT_ACTIONS(isxContext).map((action) => (
          <MenuItem
            key={action.id}
            onClick={() => {
              setStackMenuAnchor(null);
              setAction(action);
            }}
          >
            <ListItemIcon>
              <action.icon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={action.label} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default StacksManagementWidget;
